import { ONE_TO_ONE_COUNSELLING_FEATURE } from '../features/rolePermissions/utils/rolePermissionConstants'

/** Canonical + legacy aliases — keep FE/BE aligned with Role Permissions matrix. */
export const ONE_TO_ONE_FEATURE_KEYS = Object.freeze([
	ONE_TO_ONE_COUNSELLING_FEATURE,
	'OneToOneCounsellingSession',
	'OneToOneSessionManagement',
])

const isDev =
	typeof process !== 'undefined' && process.env.NODE_ENV === 'development'

function logPermissionDebug(event, detail) {
	if (!isDev) return
	// eslint-disable-next-line no-console
	console.debug(`[Permissions] ${event}`, detail)
}

export function normalizeAppPermissions(appFeatures = {}) {
	if (!appFeatures || typeof appFeatures !== 'object' || Array.isArray(appFeatures)) {
		return {}
	}

	return Object.entries(appFeatures).reduce((permissions, [feature, actions]) => {
		if (!feature) return permissions
		const featureActions = Array.isArray(actions) ? actions : []
		permissions[feature] = {
			view: featureActions.includes('view'),
			edit: featureActions.includes('edit'),
			delete: featureActions.includes('delete'),
		}
		return permissions
	}, {})
}

export function hasFeatureAction(appPermissions, feature, action = 'view') {
	if (!feature || !action) return false
	return appPermissions?.[feature]?.[action] === true
}

export function hasAnyFeatureAction(appPermissions, features = [], action = 'view') {
	return (features || []).some((feature) => hasFeatureAction(appPermissions, feature, action))
}

/**
 * Prefer live Redux permissions; fall back to encrypted localStorage user.appFeatures
 * (covers first paint before /permissions/me returns).
 */
export function getEffectiveAppPermissions(appPermissions, storedUser) {
	const fromStore = appPermissions && typeof appPermissions === 'object' ? appPermissions : {}
	if (Object.keys(fromStore).length > 0) return fromStore
	return normalizeAppPermissions(storedUser?.appFeatures)
}

export function areAppPermissionsReady(appPermissions, storedUser) {
	const effective = getEffectiveAppPermissions(appPermissions, storedUser)
	return Object.keys(effective).length > 0
}

/**
 * One-to-One Guided Session (Individual Case "Open" banner).
 * - view → menu/route visibility
 * - edit → Open button + start/listen APIs (backend requireFeatureAction edit)
 */
export function canUseOneToOneCounselling(appPermissions, level = 'edit') {
	const action = level === 'view' || level === 'edit' || level === 'delete' ? level : 'edit'
	const allowed = hasAnyFeatureAction(appPermissions, ONE_TO_ONE_FEATURE_KEYS, action)
	logPermissionDebug('one_to_one_check', {
		action,
		allowed,
		feature: ONE_TO_ONE_COUNSELLING_FEATURE,
		matched: ONE_TO_ONE_FEATURE_KEYS.filter((key) => hasFeatureAction(appPermissions, key, action)),
	})
	return allowed
}

/** True when Guided Session Open / Start should be shown (hide entirely if false). */
export function canOpenOneToOneSession(appPermissions, options = {}) {
	const { permissionsReady = true } = options
	if (!permissionsReady) return false
	return canUseOneToOneCounselling(appPermissions, 'edit')
}

/**
 * Generic feature gate used by routes/menus.
 * Prefer this over ad-hoc role name checks so Role Permissions stays the source of truth.
 */
export function canAccessFeature(appPermissions, feature, action = 'view') {
	return hasFeatureAction(appPermissions, feature, action)
}
